import {
	Query,
	QueryFilters,
	QueryKey,
	useQueryClient,
} from "@tanstack/react-query"
import {
	RealtimeEventName,
	RealtimeEventPayloadMap,
} from "modules/realtime/RealtimeProvider"
import { useRef } from "react"
import { useRealtimeSubscription } from "./useRealtimeSubscription"

export type QueryAction = "invalidate" | "remove" | "refetch" | "reset"

export interface InvalidateOnRealtimeEventOptions<
	E extends RealtimeEventName = RealtimeEventName,
	TQueryFnData = unknown,
	TError = unknown,
	TData = unknown,
	TQueryKey extends QueryKey = QueryKey,
> {
	/** Query key to invalidate/remove/refetch/reset */
	queryKey: TQueryKey
	/** Whether the queryKey must match exactly */
	exact?: boolean
	/** Whether the subscription/invalidation is active */
	enabled?: boolean
	/** Event filter predicate evaluated on the incoming realtime event data */
	predicate?: (data: RealtimeEventPayloadMap[E], eventName: E) => boolean
	/** Cache entry predicate evaluated against each query in the TanStack cache */
	queryPredicate?: (
		query: Query<TQueryFnData, TError, TData, TQueryKey>,
		data: RealtimeEventPayloadMap[E],
		eventName: E,
	) => boolean
	/** Action to perform: defaults to 'invalidate' */
	action?: QueryAction
	/** Side-effect callback invoked after the query action is executed */
	onInvalidate?: (
		data: RealtimeEventPayloadMap[E],
		eventName: E,
	) => void | Promise<void>
}

export function useInvalidateOnRealtimeEvent<
	E extends RealtimeEventName = RealtimeEventName,
	TQueryFnData = unknown,
	TError = unknown,
	TData = unknown,
	TQueryKey extends QueryKey = QueryKey,
>(
	events: E | readonly E[],
	options: InvalidateOnRealtimeEventOptions<
		E,
		TQueryFnData,
		TError,
		TData,
		TQueryKey
	>,
): void {
	const queryClient = useQueryClient()
	const {
		queryKey,
		exact,
		enabled = true,
		predicate,
		queryPredicate,
		action = "invalidate",
		onInvalidate,
	} = options

	const queryPredicateRef = useRef(queryPredicate)
	queryPredicateRef.current = queryPredicate

	const onInvalidateRef = useRef(onInvalidate)
	onInvalidateRef.current = onInvalidate

	useRealtimeSubscription(
		events,
		async (data, eventName) => {
			const currentQueryPredicate = queryPredicateRef.current
			const queryFilters: QueryFilters = {
				queryKey,
				exact,
				...(currentQueryPredicate
					? {
							predicate: (query) =>
								currentQueryPredicate(
									query as unknown as Query<
										TQueryFnData,
										TError,
										TData,
										TQueryKey
									>,
									data,
									eventName,
								),
						}
					: {}),
			}

			switch (action) {
				case "remove":
					queryClient.removeQueries(queryFilters)
					break
				case "refetch":
					await queryClient.refetchQueries(queryFilters)
					break
				case "reset":
					await queryClient.resetQueries(queryFilters)
					break
				case "invalidate":
				default:
					await queryClient.invalidateQueries(queryFilters)
					break
			}

			if (onInvalidateRef.current) {
				await onInvalidateRef.current(data, eventName)
			}
		},
		{
			enabled,
			predicate,
		},
	)
}
